"use client";

import { useQueryClient } from "@tanstack/react-query";
import type { QueryClient, QueryKey } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useSyncExternalStore } from "react";

import { HUB_POLL_INTERVALS_MS } from "@/dashboard/features/hub/lib/constants";
import {
  coordinateCustomerListRefresh,
  type CustomerListRefreshReason,
} from "@/dashboard/features/hub/lib/customer-list-refresh-coordinator";

type RefetchType = "active" | "inactive" | "all" | "none";

type RefreshRequestOptions = {
  /** Explicitly override the registered flag state for callers outside the list hook. */
  enabled?: boolean;
  /** Force a refresh after the user accepted the pending refresh affordance. */
  force?: boolean;
  /** Preserve mutation-specific invalidation semantics, such as refetchType="none". */
  refetchType?: RefetchType;
  /** Select list caches that are not representable by one contiguous query-key prefix. */
  predicate?: (queryKey: QueryKey) => boolean;
  /** Local optimistic mutations can invalidate without creating a new-activity affordance. */
  markPending?: boolean;
  /** Low-cardinality source used by refresh diagnostics. */
  reason?: CustomerListRefreshReason;
};

export type CustomerListRefreshBudgetState = {
  atTop: boolean;
  pending: boolean;
};

type Registration = {
  enabled: boolean;
  queryKey: QueryKey;
};

const coordinators = new WeakMap<QueryClient, CustomerListRefreshBudgetCoordinator>();

function sameQueryKey(left: QueryKey | null, right: QueryKey): boolean {
  return (
    !!left && left.length === right.length && left.every((part, index) => part === right[index])
  );
}

class CustomerListRefreshBudgetCoordinator {
  private readonly listeners = new Set<() => void>();
  private readonly registrations = new Map<symbol, Registration>();
  private scopeKey: QueryKey | null = null;
  private snapshot: CustomerListRefreshBudgetState = { atTop: true, pending: false };

  getSnapshot = (): CustomerListRefreshBudgetState => this.snapshot;

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  register(token: symbol, registration: Registration): void {
    const previous = this.registrations.get(token);
    this.registrations.set(token, registration);
    if (!previous || !sameQueryKey(previous.queryKey, registration.queryKey)) {
      this.setScope(registration.queryKey);
    }
  }

  unregister(token: symbol): void {
    this.registrations.delete(token);
    if (!this.registrationsHasEnabledEntry()) {
      this.updateSnapshot({ pending: false });
    }
  }

  isEnabled(): boolean {
    return this.registrationsHasEnabledEntry();
  }

  setAtTop(atTop: boolean): void {
    this.updateSnapshot({ atTop });
  }

  clearPending(): void {
    this.updateSnapshot({ pending: false });
  }

  markPending(): void {
    this.updateSnapshot({ pending: true });
  }

  private setScope(queryKey: QueryKey): void {
    if (sameQueryKey(this.scopeKey, queryKey)) {
      return;
    }
    this.scopeKey = [...queryKey];
    this.updateSnapshot({ atTop: true, pending: false });
  }

  private registrationsHasEnabledEntry(): boolean {
    return [...this.registrations.values()].some(registration => registration.enabled);
  }

  private updateSnapshot(next: Partial<CustomerListRefreshBudgetState>): void {
    const nextSnapshot = { ...this.snapshot, ...next };
    if (
      nextSnapshot.atTop === this.snapshot.atTop &&
      nextSnapshot.pending === this.snapshot.pending
    ) {
      return;
    }
    this.snapshot = nextSnapshot;
    for (const listener of this.listeners) {
      listener();
    }
  }
}

function getCoordinator(queryClient: QueryClient): CustomerListRefreshBudgetCoordinator {
  let coordinator = coordinators.get(queryClient);
  if (!coordinator) {
    coordinator = new CustomerListRefreshBudgetCoordinator();
    coordinators.set(queryClient, coordinator);
  }
  return coordinator;
}

function invalidate(
  queryClient: QueryClient,
  queryKey: QueryKey,
  refetchType?: RefetchType,
  predicate?: (queryKey: QueryKey) => boolean
): Promise<void> {
  return queryClient.invalidateQueries({
    ...(predicate ? { predicate: query => predicate(query.queryKey) } : { queryKey }),
    ...(refetchType ? { refetchType } : {}),
  });
}

function isDocumentHidden(): boolean {
  return typeof document !== "undefined" && document.visibilityState === "hidden";
}

/**
 * Request a customer-list refresh through the shared budget.
 *
 * The budget is intentionally scoped to the active tenant/list prefix. At the top, all matching
 * infinite caches are reduced to page zero before invalidation. While scrolled or hidden, the
 * query is only marked stale and the coordinator exposes a pending refresh for the sidebar control.
 */
export function requestCustomerListRefresh(
  queryClient: QueryClient,
  queryKey: QueryKey,
  options: RefreshRequestOptions = {}
): Promise<void> {
  const coordinator = getCoordinator(queryClient);
  const budgetEnabled = options.enabled ?? coordinator.isEnabled();

  // Socket events can arrive while the tab is hidden. Defer those too so background tabs do not
  // issue HTTP reads; the visibilitychange listener refreshes when the tab becomes visible.
  if (budgetEnabled && !options.force && (!coordinator.getSnapshot().atTop || isDocumentHidden())) {
    if (options.markPending !== false) {
      coordinator.markPending();
    }
    return invalidate(queryClient, queryKey, "none", options.predicate);
  }

  // A refetchType="none" invalidation is deliberately used by optimistic mutations whose
  // response already patched the visible row. Dropping cursor pages here would lose visible
  // rows without a follow-up request to replace them.
  if (options.refetchType === "none") {
    return invalidate(queryClient, queryKey, options.refetchType, options.predicate);
  }

  coordinator.clearPending();
  return coordinateCustomerListRefresh(queryClient, {
    predicate: options.predicate,
    queryKey,
    reason: options.reason ?? "unknown",
    refetchType: options.refetchType,
    trim: budgetEnabled,
  });
}

/** Hook used by the list query and sidebar controller to share one QueryClient coordinator. */
export function useCustomerListRefreshBudget({
  enabled,
  queryKey,
  schedule = false,
  intervalMs = HUB_POLL_INTERVALS_MS.customersList,
}: {
  enabled: boolean;
  queryKey: QueryKey;
  schedule?: boolean;
  intervalMs?: number;
}) {
  const queryClient = useQueryClient();
  const coordinator = getCoordinator(queryClient);
  const tokenRef = useRef<symbol | null>(null);
  if (!tokenRef.current) {
    tokenRef.current = Symbol("customer-list-refresh-budget");
  }
  const token = tokenRef.current;
  if (!token) {
    throw new Error("Customer list refresh budget coordinator token was not created");
  }

  useEffect(() => {
    coordinator.register(token, { enabled, queryKey });
    return () => coordinator.unregister(token);
  }, [coordinator, enabled, queryKey, token]);

  useEffect(() => {
    if (!enabled || !schedule) {
      return;
    }

    const refresh = (reason: CustomerListRefreshReason) => {
      if (document.visibilityState !== "visible") {
        return;
      }
      void requestCustomerListRefresh(queryClient, queryKey, { enabled, reason });
    };
    const safetyRefresh = () => refresh("safety");
    const visibilityRefresh = () => refresh("visibility");
    const onlineRefresh = () => refresh("online");
    const interval = window.setInterval(safetyRefresh, intervalMs);
    document.addEventListener("visibilitychange", visibilityRefresh);
    window.addEventListener("online", onlineRefresh);
    return () => {
      window.clearInterval(interval);
      document.removeEventListener("visibilitychange", visibilityRefresh);
      window.removeEventListener("online", onlineRefresh);
    };
  }, [enabled, intervalMs, queryClient, queryKey, schedule]);

  const state = useSyncExternalStore(
    coordinator.subscribe,
    coordinator.getSnapshot,
    coordinator.getSnapshot
  );
  const setAtTop = useCallback((atTop: boolean) => coordinator.setAtTop(atTop), [coordinator]);
  const clearPending = useCallback(() => coordinator.clearPending(), [coordinator]);
  const refreshNow = useCallback(() => {
    coordinator.setAtTop(true);
    return requestCustomerListRefresh(queryClient, queryKey, {
      enabled,
      force: true,
      reason: "explicit",
    });
  }, [coordinator, enabled, queryClient, queryKey]);

  return {
    ...state,
    setAtTop,
    clearPending,
    refreshNow,
  };
}
